<template lang="html">
  <div class="projects">
    <transition-group :duration="200 + (50 * pageSize)" class="content-wrapper" tag="div" @before-enter="setDelay" @after-enter="cleanup">
      <div v-show="loadedProjects.length > 0" v-for="(project, index) in loadedProjects" class="project-wrapper" :data-index="index" :key="project._id">
        <DitProjectAvatar :avatar="project.avatar" @click="setActiveProject(project)" />
        <p>{{project.name}}</p>
      </div>
      <div v-show="initialPageLoaded && loadedProjects.length === 0" class="empty-state" key="emptyState">
        <h2>Currently, there are no projects.</h2>
        <p>Add some to better organise your tasks!</p>
      </div>
    </transition-group>
    <transition>
      <IntersectionGuard v-if="nextPageAvailable && initialPageLoaded" key="intersector" :root="this.$el" root-margin="0px 0px 100px 0px" @enter="fetchProjectsPage">
        <DitButton class="load-more-button" key="lmb" @click="fetchProjectsPage">Load More</DitButton>
      </IntersectionGuard>
    </transition>
  </div>
</template>

<script>
import db from '@/db';
import DitButton from '@/components/DitButton.vue';
import DitProjectAvatar from '@/components/DitProjectAvatar.vue';
import IntersectionGuard from '@/components/IntersectionGuard.vue';

export default {
  components: {
    DitButton,
    DitProjectAvatar,
    IntersectionGuard, // eslint-disable-line
  },
  async created() {
    await this.fetchProjectsPage();
    this.initialPageLoaded = true;
  },
  data() {
    return {
      initialPageLoaded: false,
      pageSize: 24,
      totalProjects: -1,
      loadedProjects: [],
      nextPageAvailable: true,
    };
  },
  methods: {
    cleanup(el) {
      el.style.removeProperty('transition-delay');
    },
    async fetchProjectsPage() {
      try {
        if (this.totalProjects < 0) this.totalProjects = await db.projects.count();
        if (this.totalProjects > 0) {
          let projects = [];
          if (this.loadedProjects.length === 0) {
            projects = await db.projects
              .orderBy('createdAt')
              .limit(this.pageSize)
              .reverse()
              .toArray();
          } else {
            projects = await db.projects
              .where('createdAt')
              .below(this.loadedProjects[this.loadedProjects.length - 1].createdAt)
              .limit(this.pageSize)
              .reverse()
              .toArray();
          }
          this.loadedProjects = this.loadedProjects.concat(projects);
          if (this.loadedProjects.length < this.totalProjects) this.nextPageAvailable = true;
          else this.nextPageAvailable = false;
        } else this.nextPageAvailable = false;
      } catch (err) {
        this.$store.commit('addToast', { message: `Could not load more project: ${err.message}`, type: 'negative' });
      }
    },
    async setActiveProject(project) {
      this.$store.commit('setActiveProject', project);
      await this.$store.dispatch('addToRecentProjects', project);
      this.$router.push({ name: 'Home' });
    },
    setDelay(el) {
      const currentPage = Math.ceil(this.loadedProjects.length / this.pageSize);
      el.style.transitionDelay = `${Math.max((el.dataset.index - this.pageSize * (currentPage - 1)) * 50, 0)}ms`; // eslint-disable-line no-param-reassign
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '../styles/breakpoints'
@require '../styles/colors'

.projects
  background-color: $text
  color: $bg

  .content-wrapper
    margin: 0 auto
    padding: 2rem 0
    position: relative
    width: 6.5rem

    @media $desktop
      margin: 1rem auto
      padding: 8rem 0

    @media screen and (min-width: 13rem)
      width: 13rem

    @media screen and (min-width: 19.5rem)
      width: 19.5rem

    @media screen and (min-width: 26rem)
      width: 26rem

    @media screen and (min-width: 32.5rem)
      width: 32.5rem

      @media $desktop
        width: 19.5rem

      @media $desktopLarge
        width: 32.5rem

    @media screen and (min-width: 39rem)
      width: 39rem

      @media $desktop
        width: 19.5rem

      @media $desktopLarge
        width: 32.5rem

    .empty-state
      text-align: center
      margin-top: 8rem
      padding: 0 1.5rem

    .project-wrapper
      margin: 1.5rem
      margin-top: 0
      text-align: center
      display: inline-block
      width: 3.5rem

      &.v-enter-active,
      &.v-leave-active
        .project-avatar
          transition: transform 200ms ease
          transition-delay: inherit

        p
          transition: opacity 200ms ease
          transition-delay: inherit

        &.v-enter,
        &.v-leave-to
          .project-avatar
            transform: scale(0)

          p
            opacity: 0

      p
        font-size: 0.75rem
        text-transform: capitalize
        white-space: nowrap
        text-overflow: ellipsis
        overflow: hidden
        margin: 0 -0.5rem

  .button
    display: block
    margin: 0 auto

    &.v-enter-active,
    &.v-leave-active
      transition: opacity 200ms ease

      &.v-enter,
      &.v-leave-to
        opacity: 0
</style>
